/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { forkJoin, Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { BASE_PATH } from '@myprofile-ui/pidx-utils'

@Injectable({
  providedIn: 'root',
})
export class PhoneService {
  private httpClient = inject(HttpClient)
  private basePath = inject(BASE_PATH)

  public getPhoneList(country = 'DE', locale = 'de-DE', header?: HttpHeaders): Observable<PhoneCommunicationDto[]> {
    const headers = header ? header.set('accept-language', locale) : new HttpHeaders({ 'accept-language': locale })
    const landlines$ = this.httpClient.get<PhoneCommunicationDto[]>(`${this.basePath}/phones?country=${country}`, {
      headers: headers,
    })

    const mobiles$ = this.httpClient.get<PhoneCommunicationDto[]>(`${this.basePath}/mobiles?country=${country}`, {
      headers: headers,
    })

    return forkJoin([landlines$, mobiles$]).pipe(
      tap(([landlines, mobiles]) => {
        if (mobiles.find((mobile) => mobile.isStandard)) {
          landlines.forEach((landline) => (landline.isStandard = false))
        }
      }),
      map(([landlines, mobiles]) => [
        ...landlines.map((phone) => ({ ...phone, phoneType: 'landline' as const, id: phone.phoneId ?? '' })),
        ...mobiles.map((mobile) => ({ ...mobile, phoneType: 'mobile' as const, id: mobile.mobileId ?? '' })),
      ]),
    )
  }

  public deletePhone(country: string, locale: string, phoneModel: PhoneCommunicationDto, header?: HttpHeaders): Observable<any> {
    const headers = header ? header.set('accept-language', locale) : new HttpHeaders({ 'accept-language': locale })
    switch (phoneModel.phoneType) {
      case 'landline':
        return this.httpClient.delete<any>(`${this.basePath}/phones/${phoneModel.id}?country=${country}`, {
          headers: headers,
        })
      case 'mobile':
        return this.httpClient.delete<any>(`${this.basePath}/mobiles/${phoneModel.id}?country=${country}`, {
          headers: headers,
        })
      default:
        throw new Error(`Unsupported phone type: ${phoneModel.phoneType}`)
    }
  }

  public updatePhone(
    country: string,
    locale: string,
    phoneModel: PhoneCommunicationDto,
    header?: HttpHeaders,
  ): Observable<PhoneCommunicationDto> {
    const headers = header ? header.set('accept-language', locale) : new HttpHeaders({ 'accept-language': locale })
    switch (phoneModel.phoneType) {
      case 'landline':
        return this.httpClient.patch<PhoneCommunicationDto>(`${this.basePath}/phones/${phoneModel.id}?country=${country}`, phoneModel, {
          headers: headers,
        })
      case 'mobile':
        return this.httpClient.patch<PhoneCommunicationDto>(`${this.basePath}/mobiles/${phoneModel.id}?country=${country}`, phoneModel, {
          headers: headers,
        })
      default:
        throw new Error(`Unsupported phone type: ${phoneModel.phoneType}`)
    }
  }

  public addPhone(
    country: string,
    locale: string,
    phoneModel: PhoneCommunicationDto,
    header?: HttpHeaders,
  ): Observable<PhoneCommunicationDto> {
    const headers = header ? header.set('accept-language', locale) : new HttpHeaders({ 'accept-language': locale })
    switch (phoneModel.phoneType) {
      case 'landline':
        return this.httpClient.post<PhoneCommunicationDto>(`${this.basePath}/phones?country=${country}`, phoneModel, {
          headers: headers,
        })
      case 'mobile':
        return this.httpClient.post<PhoneCommunicationDto>(`${this.basePath}/mobiles?country=${country}`, phoneModel, {
          headers: headers,
        })
      default:
        throw new Error(`Unsupported phone type: ${phoneModel.phoneType}`)
    }
  }
}
export interface PhoneCommunicationDto {
  phoneId?: string
  mobileId?: string
  id: string
  isPending?: boolean
  isStandard?: boolean
  isVerified?: boolean
  number?: string
  isPorscheId?: boolean
  phoneType?: 'landline' | 'mobile'
  usage?: 'work' | 'home'
}
